import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpResponse, HttpErrorResponse, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { from } from 'rxjs';
import { AuthenticationService } from '../shared/services/authentication.service';
import { map, catchError } from 'rxjs/operators';
import { ModalController, PopoverController, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AppCacheStorageService } from '../shared/services/storage/app-cache-storage.service';
import { UserStorageService } from '../shared/services/storage/user-storage.service';
import { SessionTimeoutService } from '../shared/services/session-timeout.service';
import { SKIP_SESSION_ACTIVITY } from './skip-auth.context';

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {

  constructor(
    private authService: AuthenticationService,
    public translate: TranslateService,
    public toastController: ToastController,
    public readonly popoverCtrl: PopoverController,
    public readonly modalController: ModalController,
    private readonly userStorage: UserStorageService,
    private cache: AppCacheStorageService,
    private readonly sessionTimeout: SessionTimeoutService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = request.url.toLowerCase();
    const isRefresh = /\/auth\/refresh(?:\?|$)/.test(url);
    const isLoginOrLogout = /\/auth\/(?:login|logout)(?:\?|$)/.test(url);
    // Authentication endpoints must not carry a token from a previous session.
    if (isLoginOrLogout) {
      return next.handle(request.clone({
        setHeaders: { 'Content-Type': 'application/json', 'x-platform': 'web' },
        headers: request.headers.delete('Authorization'),
      }));
    }
    if (!request.context.get(SKIP_SESSION_ACTIVITY) && !isRefresh && !/\/auth\/(?:login|logout)(?:\?|$)/.test(url)) {
      this.sessionTimeout.recordRequestActivity();
    }
    return from(this.handleAccess(request, next));
  }

  private async handleAccess(request: HttpRequest<any>, next: HttpHandler):
    Promise<HttpEvent<any>> {
    const token = await this.authService.getTokenFromDevice();
    //console.log('token from storage:', token);
    let authReq;
    if (token) {
      authReq = request.clone({ setHeaders: { Authorization: token,  'Content-Type': 'application/json', 'x-platform': 'web'} });
      return next.handle(authReq).pipe(
        map((event: HttpEvent<any>) => {
          if (event instanceof HttpResponse) {
            //console.log('event--->>>', event);
            if (event.headers.get('x-new-access-token') && event.headers.get('x-new-access-token') != '') {
              //console.log('SAVING NEW TOKEN')
              this.userStorage.saveToken(event.headers.get('x-new-access-token'));
            }
          }
          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          //console.log('REQ ERROR--->>>', error);
          if (error.status === 401) {
            this.sessionTimeout.expire(error.error?.message || 'Your session has expired.');
          }
          if(error.status != 401) return throwError(() => error);

          return throwError(() => error); 
        })
      ).toPromise();
    } else {
      authReq = request.clone({ setHeaders: { 'Content-Type': 'application/json', 'x-platform': 'web'} });
      return next.handle(authReq).toPromise();
    }
  }
  
  /* close all popups (popover and modal)  on logout*/
  async dismissPopoversAndModals() {
    let popoverElement = await this.popoverCtrl;
    if (popoverElement) {
        popoverElement.dismiss();
    }
    let modalElement = await this.modalController;
    if (modalElement) {
        modalElement.dismiss();  
    }
    return;
  }

  private async presentToastLang(message) {
    const toast = await this.toastController.create({
      message,
      color: 'warning',
      duration: 10000,
      position: 'middle',
      buttons: [
        {
          text: this.translate.instant('Close'),
          role: 'cancel',
        }
      ]
    });
    toast.present();
  }
}